/**
 * Global environment selection (W1b → W1 S2a: adapter over ScopeContext).
 *
 * This used to be the one source of truth for "which environment am I
 * looking at?", storing it in its own component state and writing `?env=`
 * to the URL as a raw side-channel history write outside the router (no
 * popstate listener, so Back left it stale — RT-16 follow-up). It is now a thin
 * adapter over ScopeContext's router-derived scope: the environment value is
 * *derived* every render from the URL (via ScopeContext's context-free
 * `useRouteScope()`), and a switch is a real router push instead of a side
 * channel write, so Back and reload now correctly restore the prior
 * environment.
 *
 * The public API is unchanged on purpose — five existing pages
 * (EnvironmentSelector, EnvironmentsHub, Costs, ReleasesHub,
 * EnvironmentDetail) call `useEnvironment()` expecting exactly this shape,
 * and none of them change for this task. `readInitialEnvironment` and
 * `envKey` are also unchanged in behavior/signature.
 *
 * No ScopeProvider is required in the tree — `useRouteScope()` only needs a
 * Router ancestor, so hub-pages-smoke and env-pages-smoke, which mount
 * `EnvironmentProvider` directly under a bare `MemoryRouter`, keep passing
 * unmodified (this is the "shared chrome tolerates absent ScopeProvider via
 * the adapter" constraint from the story's round-2 red-team amendments).
 *
 * On every real switch we still drop all cached React-Query data (RT-3) so
 * one environment's numbers can never bleed through to another.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ENVIRONMENT_ID_PARAM, LEGACY_ENV_PARAM, useRouteScope } from './ScopeContext';

export type Environment = 'dev' | 'test' | 'uat' | 'prod';
export const ENVIRONMENTS: Environment[] = ['dev', 'test', 'uat', 'prod'];

// Environments that actually have a provisioned spoke today. UAT/PROD are Phase-2
// and render as disabled "not provisioned" in the selector. W2 replaces this
// constant with a live provisioned-matrix endpoint (see plan §5.2 / RT-16).
export const PROVISIONED_ENVIRONMENTS: Environment[] = ['dev', 'test'];

const STORAGE_KEY = 'dashboard.environment';
const DEFAULT_ENV: Environment = 'dev';

function isEnv(v: unknown): v is Environment {
  return typeof v === 'string' && (ENVIRONMENTS as string[]).includes(v);
}

function readStoredEnvironment(): Environment | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isEnv(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/** URL ?environmentId= or (legacy) ?env= > persisted localStorage > default (dev). Unchanged precedence/behavior (RT-16). */
export function readInitialEnvironment(): Environment {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(ENVIRONMENT_ID_PARAM) ?? params.get(LEGACY_ENV_PARAM);
    if (isEnv(fromUrl)) return fromUrl;
  } catch {
    /* URL unavailable — fall through */
  }
  return readStoredEnvironment() ?? DEFAULT_ENV;
}

interface EnvironmentContextValue {
  environment: Environment;
  setEnvironment: (env: Environment) => void;
  environments: Environment[];
  provisioned: Environment[];
  /**
   * True only when the URL really carries an environment.
   *
   * `environment` above is deliberately never null, because a dozen pages read
   * it and need a concrete value. But that fallback chain
   * (`fromScope ?? stored ?? 'dev'`) means it reports an environment even when
   * the URL names none — and the header selector was highlighting THAT, so it
   * showed a solid TEST while the scope ribbon correctly read "All environments
   * · read-only". Two controls, disagreeing, one of them showing a value that
   * came from localStorage rather than the page you were looking at. That is
   * the "silent stale browser default" the G01 contract exists to forbid.
   *
   * So: consumers that need a value keep using `environment`; anything that
   * DISPLAYS the current scope must check this first and say "All" when false.
   */
  hasExplicitEnvironment: boolean;
  /** Clear the environment from the URL — the estate-wide, read-only scope. */
  clearEnvironment: () => void;
}

const EnvironmentContext = createContext<EnvironmentContextValue | undefined>(undefined);

export const EnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const scope = useRouteScope();

  const fromScope = isEnv(scope.environmentId) ? scope.environmentId : undefined;
  const environment: Environment = fromScope ?? readStoredEnvironment() ?? DEFAULT_ENV;

  // Tracks the last environment we already reacted to, so the RT-3 cache
  // purge fires only on a genuine change — never on mount, and never twice
  // for the same value (setScope's own no-op guard already skips the
  // navigation for a same-value switch, but this guards the derived value
  // too in case something else changes the URL).
  const prevEnvRef = useRef(environment);

  useEffect(() => {
    if (prevEnvRef.current !== environment) {
      // RT-3: purge cached query data so the new env refetches cleanly and
      // no previous-env values are shown under the new label.
      queryClient.removeQueries();
      prevEnvRef.current = environment;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, environment);
    } catch {
      /* ignore */
    }
  }, [environment, queryClient]);

  const setEnvironment = useCallback(
    (env: Environment) => scope.setEnvironmentId(env),
    [scope.setEnvironmentId],
  );
  const clearEnvironment = useCallback(() => scope.setEnvironmentId(null), [scope.setEnvironmentId]);

  return (
    <EnvironmentContext.Provider
      value={{
        environment,
        setEnvironment,
        environments: ENVIRONMENTS,
        provisioned: PROVISIONED_ENVIRONMENTS,
        hasExplicitEnvironment: fromScope !== undefined,
        clearEnvironment,
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};

export function useEnvironment(): EnvironmentContextValue {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) throw new Error('useEnvironment must be used within an EnvironmentProvider');
  return ctx;
}

/**
 * Env-keyed query-key factory (RT-3). Prefix every environment-scoped React-Query
 * key with the active environment so the cache is partitioned per env and a switch
 * can never serve stale cross-env data. Pages adopt this as they migrate.
 *   useQuery({ queryKey: envKey(environment, 'costs', period), ... })
 */
export function envKey(environment: Environment, ...parts: unknown[]): unknown[] {
  return ['env', environment, ...parts];
}
